<template>
  <div class="help-resources column">
    <h4 class="is-heading-4 help-resources--title">
      <slot name="title">
        {{ t("helpResources.needHelp") }}
      </slot>
    </h4>
    <div class="columns is-desktop">
      <TCard
        v-for="(card, index) in resources"
        :key="index"
        :fullHeight="true"
        :title="card?.title"
        class="column"
      >
        <template #subtitle>
          <p class="help-resources--description">
            {{ card.description }}
          </p>
        </template>
        <template #title-right>
          <div class="help-resources--title-icon">
            <template v-if="card.cardIcon?.includes('scale-icon')">
              <span v-html="card.cardIcon"></span>
            </template>
            <scale-icon
              v-else
              :accessibility-title="card.cardIcon"
              :name="card.cardIcon"
              size="24"
            />
          </div>
        </template>
        <template #footer>
          <scale-tooltip
            v-if="card.tooltip?.length"
            :content="card.tooltip"
            placement="top"
            :disabled="card.tooltipDisabled"
          >
            <scale-button
              :href="card.buttonLink"
              target="_blank"
              class="footer-button"
              size="small"
              variant="secondary"
              :disabled="card.isButtonDisabled"
              @click="emits('button-click', card)"
            >
              {{ card.buttonText }}
              <scale-icon-navigation-right />
            </scale-button>
          </scale-tooltip>
          <scale-button
            v-else
            :href="card.buttonLink"
            class="footer-button"
            size="small"
            target="_blank"
            variant="secondary"
            :disabled="card.isButtonDisabled"
            @click="emits('button-click', card)"
          >
            {{ card.buttonText }}
            <scale-icon-navigation-right />
          </scale-button>
        </template>
      </TCard>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "vue-i18n"
import TCard from "@/components/global/TCard/TCard.vue"
import { PropType, computed } from "vue"

const { t, locale } = useI18n()

const props = defineProps({
  resources: {
    type: Array as PropType<
      {
        id?: string
        title: string
        description: string
        buttonText: string
        cardIcon?: string
        buttonLink?: string
        isButtonDisabled?: boolean
        tooltip?: string
        tooltipDisabled?: boolean
      }[]
    >,
    default: null
  }
})

const emits = defineEmits<{
  (eventName: "button-click", payload: any): void
}>()

const resources = computed(() => {
  return (
    props.resources || [
      {
        title: t("helpResources.FAQ"),
        description: t("helpResources.FAQDescription"),
        buttonText: t("helpResources.browse"),
        cardIcon: "support-chat",
        buttonLink: `https://docs.dih.telekom.com/${locale.value}/home`
      },
      {
        title: t("helpResources.demos"),
        description: t("helpResources.demosDescription"),
        buttonText: t("helpResources.browse"),
        cardIcon: "video",
        buttonLink: `https://dih.telekom.com/${locale.value}/demos`
      },
      {
        title: t("helpResources.glossary"),
        description: t("helpResources.glossaryDescription"),
        buttonText: t("helpResources.browse"),
        cardIcon: "news",
        buttonLink: `https://dih.telekom.com/${locale.value}/glossary`
      }
    ]
  )
})
</script>

<style lang="scss" scoped>
@use "@/styles/mixins/typography.scss" as *;

.help-resources {
  border-radius: var(--scl-spacing-12);
  padding: var(--scl-spacing-24);
  background: var(--telekom-color-background-surface-subtle);

  &--title {
    text-align: center;
    padding-bottom: var(--scl-spacing-32);
  }

  &--description {
    @include tg-body;

    padding-top: var(--scl-spacing-8);
  }

  &--title-icon {
    min-width: var(--scl-spacing-48);
    height: var(--scl-spacing-48);
    border-radius: var(--scl-spacing-8);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--telekom-color-background-surface-subtle);
  }

  .footer-button {
    padding-top: var(--scl-spacing-12);
  }
}
</style>
